/**
 * Reading a project's capitalisation, for the chart that colours by it.
 *
 * THIS MIRRORS fast/app/capex.py AND MUST KEEP MIRRORING IT.
 *
 * That duplication is deliberate and it is worth being explicit about why, because
 * "two implementations of one rule" is normally the thing to avoid. The server is the
 * authority: it produces the report and the CSV, and those are what anybody acts on.
 * What the chart needs is a colour per bar for lanes already in the browser, and
 * asking the API per phase would be a request per bar to answer a question the client
 * already has every input for.
 *
 * The drift is bounded by where the inputs come from. The list of capitalisable phase
 * names is NOT written here - it is fetched from `GET /api/finance/capex-phases` and
 * passed in - so the half most likely to change is still served from the one place
 * that holds it. What is reimplemented is only the combining rule, which is three
 * lines and has not changed since it was written.
 *
 * THE THREE STATES ARE THE POINT. `capex` is `true | false | null`, and null is not a
 * quieter false: it means nobody has looked. A chart that drew unreviewed work in the
 * same grey as reviewed-and-expensed work would report a clean desk that nobody has
 * sat at, which is the single most misleading thing this view could do.
 */

import type { CapexMark } from '../styles/theme';
import type { CapexPhase, Phase, Project } from '../types';

/** A phase name reduced to the form the policy is written in. Mirrors normalise(). */
export function normalise(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

/**
 * The capitalisable phase names, as a set, from what the API served.
 *
 * Built once per render of the chart rather than per bar: a lane with six phases in a
 * roadmap of forty lanes is a few hundred lookups, and a linear scan of the policy
 * array for each would be the kind of slow nobody profiles because each step is tiny.
 */
export function capitalNames(policy: readonly CapexPhase[]): Set<string> {
  return new Set(policy.filter((p) => p.capital).map((p) => normalise(p.name)));
}

/** How a project as a whole is classified. The three states, named. */
export function projectMark(project: Pick<Project, 'capex'>): CapexMark {
  if (project.capex === true) {
    return 'capital';
  }
  if (project.capex === false) {
    return 'not-capital';
  }
  // Covers both null and absent. An older backend omits the field, and absent has to
  // mean "nobody has said" rather than "not capital".
  return 'unreviewed';
}

/**
 * How ONE phase is classified: both halves of the rule, as the server applies them.
 *
 * A phase is capital only when its project is capital AND the phase is one of the
 * building stages. Structural phases - the Maintenance band every lane carries - are
 * excluded outright: ongoing support does not build an asset whatever it is named,
 * and the server says so with the same check in the same order.
 */
export function phaseMark(
  project: Pick<Project, 'capex'>,
  phase: Pick<Phase, 'name' | 'structural'>,
  capital: ReadonlySet<string>
): CapexMark {
  const mark = projectMark(project);
  if (mark !== 'capital') {
    // Unreviewed stays unreviewed all the way down. A phase of a project nobody has
    // classified is not expensed work - it is work nobody has classified, and saying
    // otherwise at the phase level would quietly answer the question at a level that
    // was never asked.
    return mark;
  }
  if (phase.structural) {
    return 'not-capital';
  }
  return capital.has(normalise(phase.name)) ? 'capital' : 'not-capital';
}

/**
 * How many phases of a lane are capital, for the row's sub-label.
 *
 * A count rather than a proportion. "3 of 7 capitalised" is a fact somebody can check
 * against the CSV; a percentage of phases is a number that looks like it means spend
 * and does not - nothing here knows what a phase cost.
 */
export function countCapital(
  project: Pick<Project, 'capex'>,
  phases: readonly Pick<Phase, 'name' | 'structural'>[],
  capital: ReadonlySet<string>
): number {
  return phases.filter((phase) => phaseMark(project, phase, capital) === 'capital').length;
}

/**
 * Phase names on this roadmap that the policy has never heard of.
 *
 * Reported in lane order with no duplicates, the same as the server's `unrecognised`.
 * They draw as expense, which is the cautious answer rather than a decision - and
 * saying so where somebody can see it is the difference between a cautious answer and
 * a silent one.
 */
export function unrecognised(
  projects: readonly Project[],
  policy: readonly CapexPhase[]
): string[] {
  const known = new Set(policy.map((p) => normalise(p.name)));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const project of projects) {
    for (const phase of project.phases) {
      if (phase.structural) {
        continue;
      }
      const key = normalise(phase.name);
      if (key && !known.has(key) && !seen.has(key)) {
        seen.add(key);
        out.push(phase.name.trim());
      }
    }
  }
  return out;
}
